import { motion, useReducedMotion } from 'framer-motion'
import { Footer } from '@/components/Footer'
import { Navbar } from '@/components/Navbar'

const ease: [number, number, number, number] = [0.16, 1, 0.3, 1]

export function TermsOfServicePage() {
    const reduced = useReducedMotion()
    const m = (d: number) => reduced ? undefined : { duration: 0.7, delay: d, ease }

    return (
        <div className="min-h-screen bg-[#211d18] text-[#eee8dc]">
            <Navbar variant="dark" />

            <main className="relative mx-auto max-w-[820px] px-6 pb-24 pt-32 sm:px-8 sm:pt-40">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={m(0)}
                >
                    <span className="font-body text-[11px] uppercase tracking-[0.2em] text-[#c8a88a]">
                        Legal
                    </span>
                    <h1 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl text-[#eee8dc]">
                        Terms of Service
                    </h1>
                    <p className="mt-3 font-body text-sm text-[#eee8dc]/40">
                        Last updated: February 2025
                    </p>

                    <div className="mt-14 space-y-10">
                        <Section title="1. Agreement to Terms">
                            <p>
                                By accessing or using Jpdz ("the Service"), a product of Jpdz Labs, you agree to
                                be bound by these Terms of Service. If you do not agree to these terms, please
                                do not use our service.
                            </p>
                        </Section>

                        <Section title="2. Description of Service">
                            <p>
                                Jpdz is an AI-powered developer platform that provides tools for retrieval,
                                memory, orchestration, and reasoning. Our products help developers build
                                faster with better context and fewer interruptions.
                            </p>
                        </Section>

                        <Section title="3. User Accounts">
                            <ul>
                                <li>You must provide accurate and complete registration information</li>
                                <li>You are responsible for maintaining the security of your account</li>
                                <li>You must be at least 13 years old to use this service</li>
                                <li>One person or legal entity may only maintain one account</li>
                            </ul>
                        </Section>

                        <Section title="4. Acceptable Use">
                            <p>You agree NOT to:</p>
                            <ul>
                                <li>Use the service for any illegal purpose</li>
                                <li>Attempt to hack, disrupt, or overload our systems</li>
                                <li>Scrape or collect data without permission</li>
                                <li>Impersonate others or provide false information</li>
                                <li>Use automated tools to access the service excessively</li>
                                <li>Share or resell access to your account</li>
                            </ul>
                        </Section>

                        <Section title="5. AI Recommendations Disclaimer">
                            <p>
                                <strong className="text-[#eee8dc]/90">Important:</strong> Our AI provides recommendations based on available
                                information and algorithms. These recommendations are for informational purposes
                                only and should not be considered professional advice.
                            </p>
                            <ul>
                                <li>We do not guarantee the accuracy of AI recommendations</li>
                                <li>Prices, availability, and product specifications may change</li>
                                <li>Always verify information before making decisions</li>
                                <li>We are not responsible for decisions made based on AI output</li>
                            </ul>
                        </Section>

                        <Section title="6. Intellectual Property">
                            <p>
                                The Jpdz service, including its design, features, and content, is owned by
                                Jpdz Labs. You may not copy, modify, or distribute our intellectual property
                                without permission.
                            </p>
                        </Section>

                        <Section title="7. User Content">
                            <p>
                                You retain ownership of content you submit (such as chat messages and device
                                information). By using our service, you grant us a license to use this content
                                to provide and improve our service.
                            </p>
                        </Section>

                        <Section title="8. Third-Party Services">
                            <p>
                                Our service may include links to third-party websites or integrate with
                                third-party services (such as Google for authentication). We are not
                                responsible for the content or practices of these third parties.
                            </p>
                        </Section>

                        <Section title="9. Termination">
                            <p>
                                We may suspend or terminate your account if you violate these terms. You may
                                delete your account at any time. Upon termination, your right to use the
                                service ends immediately.
                            </p>
                        </Section>

                        <Section title="10. Limitation of Liability">
                            <p>
                                To the maximum extent permitted by law, Jpdz Labs shall not be liable for any
                                indirect, incidental, special, consequential, or punitive damages arising from
                                your use of the service.
                            </p>
                        </Section>

                        <Section title="11. Indemnification">
                            <p>
                                You agree to indemnify and hold harmless Jpdz Labs from any claims, damages,
                                or expenses arising from your use of the service or violation of these terms.
                            </p>
                        </Section>

                        <Section title="12. Changes to Terms">
                            <p>
                                We may modify these terms at any time. Continued use of the service after
                                changes constitutes acceptance of the new terms. We will notify you of
                                significant changes via email or in-app notification.
                            </p>
                        </Section>

                        <Section title="13. Governing Law">
                            <p>
                                These terms shall be governed by the laws of the United States. Any disputes
                                shall be resolved in the courts of competent jurisdiction.
                            </p>
                        </Section>

                        <Section title="14. Contact">
                            <p>
                                For questions about these Terms of Service, contact us at:
                            </p>
                            <p className="text-[#c8a88a]">
                                ratnam@jpdz.app
                            </p>
                        </Section>
                    </div>
                </motion.div>
            </main>

            <Footer />
        </div>
    )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="border-t border-[#eee8dc]/[0.06] pt-8">
            <h2 className="font-serif text-lg sm:text-xl text-[#eee8dc]/90 mb-4">{title}</h2>
            <div className="font-body text-[15px] leading-[1.8] text-[#eee8dc]/55 space-y-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_li]:text-[#eee8dc]/50">
                {children}
            </div>
        </section>
    )
}
